// Avatar maker transport. A FORK of finewood's Host Maker transport
// (packages/host/src/hostMaker/cloudClient.ts + shared/jobs.ts, and
// screens/hostMaker/flow/labClient.ts + flowMock.ts for the lab), owned here
// afterwards, no runtime coupling — the same way resume-maker-2 forked it.
//
//   lab   a REAL build on the Thursday Night backend (the e2e test avatar, a mustard sweater from the
//         photo, 5090, 2026-10-17) replayed on its own clock as a labelled time-lapse, side/back
//         views included. No network, no spend, no DB write. `&lab=1`.
//   live  Thursday Night's OWN fork of that backend on the ThursdayNight Supabase project
//         (web/schedule/supabase/avatar_maker.sql: RPCs avatar_reserve / avatar_build_start, bucket
//         `bfn-avatars`, polling bfn_avatar_jobs; web/schedule/avatar-worker builds on the host PC's
//         GPU), with the same request/response shapes as finewood's host_build_* calls.
//         Plain fetch against PostgREST / Storage: no supabase-js; the site's own sign-in.
//         The default (owner approved it live, 2026-10-17); `&lab=1` replays instead.

export interface Take { seed: number; url: string }
export type BuildStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export interface Build { status: BuildStatus; step: number; total: number; stage?: string; frames: Record<string, string>; error?: string }
/** A finished pack: every deployed frame { tag: url }, and the manifest the game reads. */
export interface Pack { base: string; ext: string; frames: Record<string, string>; manifest: Record<string, unknown> }
/** The outfit is what they wear in the photo (owner, 2026-10-17): no presets. */
export type Outfit = 'photo';

export interface AvatarClient {
  mode: 'lab' | 'live';
  /** lab only: how many times faster than the real build the replay runs. */
  timelapse?: number;
  sketch(input: { photo: File; name: string; label: string; outfit: Outfit; seeds: number[] }, onTake: (t: Take) => void): Promise<Take[]>;
  build(input: { name: string; label: string; take: number }, onProgress: (b: Build) => void): Promise<Pack>;
}

/** The new frame tags (2026-10-17): the same pack, five more views. The `_l` / `_r` suffix is the
 *  side of the IMAGE the face points to; the `_r` / `_l` twins may be mirrors (manifest viewOf). */
export const VIEW_TAGS = ['three_quarter_l', 'side_l', 'back', 'side_r', 'three_quarter_r'] as const;

/** The avatar's name: per user on Thursday Night (bfn_avatars is keyed by owner + slug). */
export function avatarId(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'guest';
}

// ---- lab ---------------------------------------------------------------------

/** Under /assets: the thursday-night.com front door proxies only /schedule + /assets to this SPA. */
const ART = '/assets/avatar-lab';
/** The real clock (ms) of that build, from the worker's files: takes landed at 82 / 105 / 129 s
 *  (a cold ComfyUI), frames as below after the build was claimed, published at 655 s. */
const SKETCH_LANDED = [82_000, 105_000, 129_000];
const BUILD_FRAMES: ReadonlyArray<readonly [number, string]> = [
  [44_000, 'handsup'], [64_000, 'welcome'], [85_000, 'pockets'], [124_000, 'eye_blink'], [126_000, 'closed'],
  [170_000, 'eye_browup'], [192_000, 'eye_browone'], [241_000, 'teeth'], [248_000, 'eh'], [313_000, 'ah'],
  [331_000, 'oh'], [339_000, 'oo'], [347_000, 'fv'], [388_000, 'point'],
  [522_000, 'three_quarter_l'], [522_100, 'three_quarter_r'], [565_000, 'side_l'], [565_100, 'side_r'], [606_000, 'back'],
];
const BUILD_DONE = 655_000;
const LAB_MANIFEST = {
  mouths: ['ah', 'closed', 'eh', 'fv', 'oh', 'oo', 'teeth'], poses: ['handsup', 'pockets', 'point', 'welcome'],
  eyes: ['eye_blink', 'eye_browone', 'eye_browup'], mouthSet: 'eight', format: 4, w: 2166, h: 1206, figure: { x: 529, w: 1017 }, ext: 'webp',
  views: [...VIEW_TAGS], viewOf: { three_quarter_r: 'three_quarter_l!mirror', side_r: 'side_l!mirror' },
};
/** The replay's speed-up (the sketches at a third of it): shown on the page, never passed off as real. */
export const LAB_TIMELAPSE = 20;
const PACK_TAGS = ['closed', 'eh', 'oh', 'oo', 'ah', 'eye_blink', ...VIEW_TAGS];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** `speed` multiplies the time-lapse (sketch ×3, build ×LAB_TIMELAPSE). */
export function labClient(speed = 1): AvatarClient {
  return {
    mode: 'lab',
    timelapse: LAB_TIMELAPSE * speed,
    async sketch(input, onTake) {
      const out: Take[] = [];
      let at = 0;
      for (let i = 0; i < input.seeds.length; i++) {
        const t = SKETCH_LANDED[Math.min(i, SKETCH_LANDED.length - 1)];
        await sleep((t - at) / (3 * speed));
        at = t;
        const take = { seed: input.seeds[i], url: `${ART}/take-${i % 3}.webp` };
        out.push(take);
        onTake(take);
      }
      return out;
    },
    async build(_input, onProgress) {
      const total = 1 + BUILD_FRAMES.length + 1;
      const frames: Record<string, string> = {};
      let at = 0;
      onProgress({ status: 'running', step: 0, total, stage: 'rendering', frames: {} });
      for (const [t, tag] of BUILD_FRAMES) {
        await sleep((t - at) / (LAB_TIMELAPSE * speed));
        at = t;
        frames[tag] = `${ART}/raw/${tag}.webp`;
        onProgress({ status: 'running', step: Object.keys(frames).length, total, stage: 'rendering', frames: { ...frames } });
      }
      onProgress({ status: 'running', step: total - 1, total, stage: 'publishing', frames: { ...frames } });
      await sleep((BUILD_DONE - at) / (LAB_TIMELAPSE * speed));
      onProgress({ status: 'completed', step: total, total, frames: { ...frames } });
      // The lab keeps the matted frames the theater shows (front, talking mouths, views); the rest show raw.
      const packFrames = { ...frames, ...Object.fromEntries(PACK_TAGS.map((t) => [t, `${ART}/pack/${t}.webp`])) };
      return { base: `${ART}/pack`, ext: 'webp', frames: packFrames, manifest: LAB_MANIFEST };
    },
  };
}

// ---- live (Thursday Night's own Supabase) -------------------------------------------

// The ThursdayNight project (rkdyfkulzvskvxivylxm): supabase/avatar_maker.sql + avatar-worker/.
// The key is the project's PUBLISHABLE key: public by design (it ships in every page that uses it);
// RLS and the RPCs' own auth.uid() checks are what gate it. The env vars override both.
const TN_URL = (import.meta.env.VITE_TN_SUPABASE_URL ?? 'https://rkdyfkulzvskvxivylxm.supabase.co').replace(/\/$/, '');
const TN_KEY = import.meta.env.VITE_TN_SUPABASE_KEY ?? 'sb_publishable_is9Y4pXTeKSws1_qqzhqkw_bK74jdxj';
const BUCKET = 'bfn-avatars';
const POLL_MS = 1500;
const WAIT_MAX_MS = 30 * 60_000;

export const liveConfigured = (): boolean => !!TN_URL && !!TN_KEY;

/** The user id in a GoTrue access token: the folder their uploads live under. */
function uidOf(token: string): string {
  const b = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return (JSON.parse(atob(b)) as { sub: string }).sub;
}

type Token = () => Promise<string>;

async function tn<T>(token: Token, path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${TN_URL}${path}`, { ...init, headers: { apikey: TN_KEY, Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json', ...(init.headers ?? {}) } });
  const body = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error(friendly((body as { message?: string } | null)?.message ?? `HTTP ${r.status}`));
  return body as T;
}

/** The RPC errors a guest can hit, in plain words. */
function friendly(m: string): string {
  if (/already being built/.test(m)) return 'This avatar is already being built. Wait for that to finish.';
  if (/not signed in|JWT/.test(m)) return 'Sign in first.';
  if (/daily build cap/.test(m)) return 'That is the daily limit for the avatar studio. Try again tomorrow.';
  if (/too many avatars/.test(m)) return 'You have a lot of avatars already.';
  return m.replace(/^avatar_\w+: /, '');
}

interface Row { id: string; status: string | null; feedback: string[] | null; error: string | null }
interface Progress { step?: number; totalSteps?: number; stage?: string; frames?: Record<string, string>; takes?: Take[] }
const progressOf = (r: Row): Progress => { try { return JSON.parse(r.feedback?.[1] ?? '{}') as Progress; } catch { return {}; } };
const statusOf = (s: string | null): BuildStatus => (s === 'processing' ? 'running' : s === 'done' ? 'completed' : s === 'failed' ? 'failed' : s === 'cancelled' ? 'cancelled' : 'queued');

/** Poll one job to its end (cloudClient.waitJob): no reads while the tab is hidden (egress). */
async function waitJob(token: Token, id: string, onRow: (r: Row) => void): Promise<Row> {
  const until = Date.now() + WAIT_MAX_MS;
  for (;;) {
    if (document.hidden && Date.now() < until) { await sleep(POLL_MS); continue; }
    const [row] = await tn<Row[]>(token, `/rest/v1/bfn_avatar_jobs?id=eq.${encodeURIComponent(id)}&select=id,status,feedback,error`);
    if (!row) throw new Error('That job vanished.');
    onRow(row);
    const s = statusOf(row.status);
    if (s === 'completed') return row;
    if (s === 'failed' || s === 'cancelled') throw new Error(row.error ?? 'The studio could not finish that.');
    if (Date.now() > until) throw new Error('The studio is taking too long. Try again in a bit.');
    await sleep(POLL_MS);
  }
}

/** `token` = the Thursday Night session's access token (auth.ts accessToken, refreshed near expiry).
 *  `eventId` = the movie night the guest came from (?e=), stored on the avatar row. */
export function liveClient(token: Token, eventId?: string | null): AvatarClient {
  const reserve = (name: string, label: string) =>
    tn(token, '/rest/v1/rpc/avatar_reserve', { method: 'POST', body: JSON.stringify({ p_id: name, p_label: label, p_event: eventId || null }) });
  const start = (name: string, input: Record<string, unknown>) =>
    tn<string>(token, '/rest/v1/rpc/avatar_build_start', { method: 'POST', body: JSON.stringify({ p_host: name, p_input: input }) });
  return {
    mode: 'live',
    async sketch(input, onTake) {
      await reserve(input.name, input.label);
      const ext = /\.(png|webp|jpe?g)$/i.exec(input.photo.name)?.[0].toLowerCase() ?? '.jpg';
      const t = await token();
      const photoPath = `${uidOf(t)}/${input.name}/in/photo_${Date.now()}${ext}`;
      const up = await fetch(`${TN_URL}/storage/v1/object/${BUCKET}/${photoPath}`, {
        method: 'POST', body: input.photo,
        headers: { apikey: TN_KEY, Authorization: `Bearer ${t}`, 'Content-Type': input.photo.type || 'image/jpeg', 'cache-control': '3600' },
      });
      if (!up.ok) throw new Error(`Upload failed (${up.status}).`);
      // 'local' = the Thursday Night avatar worker on the host PC's GPU (the only provider).
      const job = await start(input.name, { phase: 'stylize', photoPath, seeds: input.seeds, outfit: input.outfit, imageProvider: 'local' });
      const seen = new Set<number>();
      const out: Take[] = [];
      const emit = (r: Row) => { for (const tk of progressOf(r).takes ?? []) if (!seen.has(tk.seed)) { seen.add(tk.seed); out.push(tk); onTake(tk); } };
      emit(await waitJob(token, job, emit));
      if (!out.length) throw new Error('No sketch came back.');
      return out;
    },
    async build(input, onProgress) {
      await reserve(input.name, input.label);
      const job = await start(input.name, { phase: 'build', take: input.take, imageProvider: 'local', format: 4 });
      await waitJob(token, job, (r) => {
        const p = progressOf(r);
        onProgress({ status: statusOf(r.status), step: p.step ?? 0, total: p.totalSteps ?? 22, stage: p.stage, frames: p.frames ?? {}, ...(r.error ? { error: r.error } : {}) });
      });
      const [h] = await tn<Array<{ pack_url: string | null; pack_ext: string | null }>>(token, `/rest/v1/bfn_avatars?slug=eq.${encodeURIComponent(input.name)}&select=pack_url,pack_ext`);
      if (!h?.pack_url) throw new Error('The avatar finished but has no pack yet. Reload in a minute.');
      const ext = h.pack_ext ?? 'webp';
      const manifest = (await (await fetch(`${h.pack_url}/manifest.json`)).json()) as Record<string, unknown>;
      const tags = new Set<string>(['closed']);
      for (const k of ['mouths', 'poses', 'eyes', 'views']) for (const t of Array.isArray(manifest[k]) ? (manifest[k] as unknown[]) : []) if (typeof t === 'string') tags.add(t);
      return { base: h.pack_url, ext, manifest, frames: Object.fromEntries([...tags].map((t) => [t, `${h.pack_url}/${t}.${ext}`])) };
    },
  };
}
